import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TRButton } from "@/components/TRButton";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { boxName, styleName } from "@/lib/catalog";

export default function Account() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [loading, user, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/orders/me")
                .then(({ data }) => setOrders(data))
                .catch(() => {})
                .finally(() => setReady(true));
        }
    }, [user]);

    if (!user) return <div className="pt-44" />;

    return (
        <div data-testid="account-page" className="pt-28 md:pt-36 min-h-[70vh]">
            <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <span className="eyebrow text-ink/50">Account</span>
                        <h1 className="mt-4 font-serif font-light text-5xl tracking-[-0.04em]">
                            Hello, {user.name}
                        </h1>
                        <p className="mt-3 text-ink/60">{user.email}</p>
                    </div>
                    <div className="flex gap-3">
                        {user.role === "admin" && (
                            <TRButton variant="ghost" onClick={() => navigate("/admin")} data-testid="to-admin">
                                Admin
                            </TRButton>
                        )}
                        <TRButton variant="outline" onClick={() => logout().then(() => navigate("/"))} data-testid="logout-btn">
                            Sign Out
                        </TRButton>
                    </div>
                </div>

                <h2 className="mt-16 eyebrow text-ink/50">Order History</h2>
                {ready && orders.length === 0 && (
                    <div className="mt-6 border border-line py-16 text-center" data-testid="no-orders">
                        <p className="font-serif text-2xl text-ink/60">No orders yet.</p>
                        <div className="mt-6 flex justify-center">
                            <TRButton as="link" to="/shop/socks/men">Start shopping</TRButton>
                        </div>
                    </div>
                )}
                <div className="mt-6 space-y-4">
                    {orders.map((o) => (
                        <div key={o.id} className="border border-line p-6" data-testid={`order-${o.id}`}>
                            <div className="flex flex-wrap justify-between gap-3">
                                <span className="text-sm text-ink/55">
                                    {new Date(o.created_at).toLocaleDateString()}
                                </span>
                                <span
                                    className={`eyebrow ${
                                        o.payment_status === "paid" ? "text-ink" : "text-ink/40"
                                    }`}
                                >
                                    {o.payment_status === "paid" ? "Paid" : "Pending"}
                                </span>
                            </div>
                            <div className="mt-4 space-y-1">
                                {o.items.map((it, idx) => (
                                    <p key={idx} className="text-sm text-ink/75">
                                        {it.quantity}× {boxName(it.category, it.boxType)} —{" "}
                                        {it.gender === "men" ? "Men" : "Women"} /{" "}
                                        {it.boxType === "starter" ? "Assorted" : styleName(it.category, it.style)} /{" "}
                                        {it.color} / {it.size}
                                    </p>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-line flex justify-between">
                                <span className="eyebrow text-ink/50">Total</span>
                                <span className="font-serif text-xl">€{o.amount}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
